export type CatalogueNavigationInteraction = {
  button: number;
  defaultPrevented: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  target?: string;
  download?: boolean;
};

export type PendingCatalogueNavigation = { sourceHref: string; targetHref: string };

function isPlainCatalogueClick(event: CatalogueNavigationInteraction) {
  return !(
    event.defaultPrevented ||
    event.button !== 0 ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.download ||
    (event.target && event.target !== '_self')
  );
}

function cataloguePath(pathname: string, locales: readonly string[]) {
  const segments = pathname.split('/').filter(Boolean);
  if (locales.includes(segments[0])) segments.shift();
  return `/${segments.join('/')}`;
}

/** Only an explicit cross-page catalogue click may own a destination scroll. */
export function getCatalogueNavigation(
  currentHref: string,
  targetHref: string,
  event: CatalogueNavigationInteraction,
  locales: readonly string[],
): PendingCatalogueNavigation | null {
  if (!isPlainCatalogueClick(event)) return null;

  try {
    const source = new URL(currentHref);
    const target = new URL(targetHref, source);
    if (
      !['http:', 'https:'].includes(target.protocol) ||
      source.origin !== target.origin ||
      target.hash ||
      cataloguePath(source.pathname, locales) === cataloguePath(target.pathname, locales)
    )
      return null;

    return { sourceHref: source.href, targetHref: target.href };
  } catch {
    return null;
  }
}

/** Preserve the existing active-link top action, without touching query or hash navigation. */
export function shouldScrollCurrentCataloguePage(
  currentHref: string,
  targetHref: string,
  event: CatalogueNavigationInteraction,
  locales: readonly string[],
) {
  if (!isPlainCatalogueClick(event)) return false;
  try {
    const current = new URL(currentHref);
    const target = new URL(targetHref, current);
    return (
      current.origin === target.origin &&
      !current.hash &&
      !target.hash &&
      current.search === target.search &&
      cataloguePath(current.pathname, locales) === cataloguePath(target.pathname, locales)
    );
  } catch {
    return false;
  }
}

export function createCatalogueNavigationState() {
  let pending: PendingCatalogueNavigation | null = null;

  return {
    begin(navigation: PendingCatalogueNavigation) {
      pending = navigation;
    },
    cancel() {
      pending = null;
    },
    hasPending() {
      return pending !== null;
    },
    /** A pathname update alone is insufficient: the rendered model must match too. */
    consume(currentHref: string, readySlug: string, locales: readonly string[]) {
      if (!pending) return false;
      try {
        const current = new URL(currentHref);
        const target = new URL(pending.targetHref);
        const source = new URL(pending.sourceHref);
        const currentPath = cataloguePath(current.pathname, locales);
        const targetPath = cataloguePath(target.pathname, locales);

        if (current.origin !== target.origin || current.hash) {
          pending = null;
          return false;
        }
        if (currentPath !== targetPath) {
          if (currentPath !== cataloguePath(source.pathname, locales)) pending = null;
          return false;
        }

        if (!readySlug || /[?#]/.test(readySlug) || readySlug.startsWith('//')) return false;
        const ready = new URL(`/${readySlug.replace(/^\/+/, '')}`, current.origin);
        if (cataloguePath(ready.pathname, locales) !== targetPath) return false;

        pending = null;
        return true;
      } catch {
        pending = null;
        return false;
      }
    },
  };
}
